/**
 * Practice Review Page — Record & Review's review surface.
 *
 * Routes:
 *   /practice/sessions/:sessionId/review   the learner (annotates)
 *   /coach/sessions/:sessionId/review      an engaged coach (read-only)
 *
 * The server decides access: a session the viewer may not see is "not found",
 * exactly like an absent one.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PracticeReviewView } from '../components/practiceReview/PracticeReviewView';
import {
  useAddPracticeAnnotationMutation,
  useDeletePracticeAnnotationMutation,
  useEditPracticeAnnotationMutation,
  useGetPracticeReviewQuery,
} from '../store/api/practiceReviewApi';

export function PracticeReviewPage({ as }: { as: 'owner' | 'coach' }) {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const { data: review, isLoading, isError } = useGetPracticeReviewQuery({ sessionId, as }, { skip: !sessionId });
  const [addNote, { isLoading: adding }] = useAddPracticeAnnotationMutation();
  const [editNote, { isLoading: editing }] = useEditPracticeAnnotationMutation();
  const [deleteNote, { isLoading: deleting }] = useDeletePracticeAnnotationMutation();
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch {
      setError('Could not save your note. Please try again.');
    }
  };

  const back = as === 'coach' ? { to: '/coach/dashboard', label: 'Back to coach dashboard' } : { to: '/practice', label: 'Back to Practice' };

  return (
    <div className="space-y-6">
      <div>
        <Link to={back.to} className="text-sm text-primary-600 hover:text-primary-700">
          ← {back.label}
        </Link>
        <h1 className="mt-2 text-2xl font-bold text-gray-900">Session review</h1>
        {review?.session.endedAt && (
          <p className="mt-1 text-gray-500">{new Date(review.session.endedAt).toLocaleString()}</p>
        )}
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        </div>
      )}

      {isError && !isLoading && (
        <div className="card">
          <div className="card-body py-12 text-center text-gray-500">
            This session's review is not available. It may not exist, may not be finished yet, or may not be shared with you.
          </div>
        </div>
      )}

      {review && (
        <PracticeReviewView
          review={review}
          saving={adding || editing || deleting}
          error={error}
          onAddNote={(segmentIndex, body) => run(() => addNote({ sessionId, segmentIndex, body }).unwrap())}
          onEditNote={(id, body) => run(() => editNote({ sessionId, id, body }).unwrap())}
          onDeleteNote={(id) => run(() => deleteNote({ sessionId, id }).unwrap())}
        />
      )}
    </div>
  );
}
